/**
 * KaivaEdge — orthogonal elbow path with rounded corners + cream label pill.
 *
 * Phase 4.2 port of design/kaiva-canvas/project/components/canvas.jsx Edge() +
 * smartPath(). Reads the relationship type from `data.canonicalType` (set by
 * useProcessCanvas) and `data.staged` for the dashed-sienna proposal styling.
 *
 * The path generator is copied byte-for-byte from the prototype so future
 * tweaks stay diffable against the design source.
 */

import { type EdgeProps, Position } from '@xyflow/react';
import { getEdgeLabel } from '@/lib/edgeLabels';
import { useCanvasOptions } from '../CanvasOptionsContext';

type Side = 'l' | 'r' | 't' | 'b';

function positionToSide(p: Position): Side {
  switch (p) {
    case Position.Left:   return 'l';
    case Position.Right:  return 'r';
    case Position.Top:    return 't';
    case Position.Bottom: return 'b';
  }
}

type Anchor = { x: number; y: number; side: Side };

/**
 * Orthogonal path with rounded corners between two anchored points.
 * Ported from prototype canvas.jsx — keep in sync with the design source.
 */
function smartPath(from: Anchor, to: Anchor): string {
  const r = 8;
  const fx = from.x, fy = from.y, tx = to.x, ty = to.y;
  const fs = from.side, ts = to.side;

  const horizFrom = fs === 'l' || fs === 'r';
  const horizTo   = ts === 'l' || ts === 'r';

  let p1: { x: number; y: number };
  let p2: { x: number; y: number };
  if (horizFrom && horizTo) {
    const midX = (fx + tx) / 2;
    p1 = { x: midX, y: fy };
    p2 = { x: midX, y: ty };
  } else if (!horizFrom && !horizTo) {
    const midY = (fy + ty) / 2;
    p1 = { x: fx, y: midY };
    p2 = { x: tx, y: midY };
  } else if (horizFrom && !horizTo) {
    p1 = { x: tx, y: fy };
    p2 = { x: tx, y: fy };
  } else {
    p1 = { x: fx, y: ty };
    p2 = { x: fx, y: ty };
  }

  const same = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;

  if (same(p1, p2)) {
    const c = p1;
    const sx = Math.sign(c.x - fx);
    const sy = Math.sign(ty - c.y);
    const r1 = Math.min(r, Math.abs(c.x - fx), Math.abs(ty - c.y));
    return `M ${fx},${fy} L ${c.x - sx * r1},${c.y} Q ${c.x},${c.y} ${c.x},${c.y + sy * r1} L ${tx},${ty}`;
  }

  const sx1 = Math.sign(p1.x - fx) || Math.sign(p2.x - p1.x);
  const sy1 = Math.sign(p1.y - fy) || Math.sign(p2.y - p1.y);
  const sx2 = Math.sign(tx - p2.x) || Math.sign(p2.x - p1.x);
  const sy2 = Math.sign(ty - p2.y) || Math.sign(p2.y - p1.y);

  const r1 =
    Math.min(
      r,
      Math.abs(p1.x - fx) || Math.abs(p1.y - fy),
      Math.abs(p2.x - p1.x) || Math.abs(p2.y - p1.y),
    ) || 0;
  const r2 =
    Math.min(
      r,
      Math.abs(tx - p2.x) || Math.abs(ty - p2.y),
      Math.abs(p2.x - p1.x) || Math.abs(p2.y - p1.y),
    ) || 0;

  if (horizFrom) {
    return `M ${fx},${fy} L ${p1.x - sx1 * r1},${p1.y} Q ${p1.x},${p1.y} ${p1.x},${p1.y + sy1 * r1} L ${p2.x},${p2.y - sy2 * r2} Q ${p2.x},${p2.y} ${p2.x + sx2 * r2},${p2.y} L ${tx},${ty}`;
  }
  return `M ${fx},${fy} L ${p1.x},${p1.y - sy1 * r1} Q ${p1.x},${p1.y} ${p1.x + sx1 * r1},${p1.y} L ${p2.x - sx2 * r2},${p2.y} Q ${p2.x},${p2.y} ${p2.x},${p2.y + sy2 * r2} L ${tx},${ty}`;
}

/**
 * Pick a midpoint that sits on the longest leg of the elbow — keeps the
 * label pill from landing on a node card.
 */
function labelMidpoint(from: Anchor, to: Anchor): { x: number; y: number } {
  const horizFrom = from.side === 'l' || from.side === 'r';
  const horizTo   = to.side   === 'l' || to.side   === 'r';

  if (horizFrom && horizTo) return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  if (!horizFrom && !horizTo) return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };

  const horizLen = Math.abs(to.x - from.x);
  const vertLen  = Math.abs(to.y - from.y);
  if (horizFrom && !horizTo) {
    return horizLen >= vertLen
      ? { x: (from.x + to.x) / 2, y: from.y }
      : { x: to.x, y: (from.y + to.y) / 2 };
  }
  return vertLen >= horizLen
    ? { x: from.x, y: (from.y + to.y) / 2 }
    : { x: (from.x + to.x) / 2, y: to.y };
}

export function KaivaEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerEnd,
}: EdgeProps) {
  const from: Anchor = { x: sourceX, y: sourceY, side: positionToSide(sourcePosition) };
  const to:   Anchor = { x: targetX, y: targetY, side: positionToSide(targetPosition) };

  const d = smartPath(from, to);

  const staged = Boolean((data as { staged?: boolean } | undefined)?.staged);
  const canonicalType = (data as { canonicalType?: string } | undefined)?.canonicalType;
  const { showLabels } = useCanvasOptions();
  const labelText = canonicalType && showLabels ? getEdgeLabel(canonicalType) : '';

  const stroke = staged ? 'hsl(var(--sienna))' : 'hsl(220 13% 65%)';
  const strokeWidth = staged ? 1.75 : 1.5;
  const dashArray = staged ? '5 4' : undefined;

  let pillCenter: { x: number; y: number } | null = null;
  let pillW = 0;
  if (labelText) {
    pillCenter = labelMidpoint(from, to);
    pillW = labelText.length * 5.6 + 14;
  }
  const pillH = 16;

  return (
    <g className={staged ? 'kaiva-edge-g staged' : 'kaiva-edge-g'}>
      <path
        d={d}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dashArray}
        markerEnd={markerEnd}
      />
      {labelText && pillCenter && (
        <g pointerEvents="none">
          <rect
            x={pillCenter.x - pillW / 2}
            y={pillCenter.y - pillH / 2}
            width={pillW}
            height={pillH}
            rx={8}
            fill={staged ? 'hsl(var(--sienna-soft))' : 'hsl(var(--background))'}
            stroke={staged ? 'hsl(var(--sienna) / 0.4)' : 'hsl(220 13% 80%)'}
            strokeWidth={1}
          />
          <text
            x={pillCenter.x}
            y={pillCenter.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={9.5}
            fontWeight={500}
            letterSpacing="0.04em"
            fill={staged ? 'hsl(var(--sienna-deep))' : 'hsl(var(--muted-foreground))'}
          >
            {labelText}
          </text>
        </g>
      )}
    </g>
  );
}
